import {
  Modal,
  Stack,
  Button,
  Group,
  Text,
  NumberInput,
  TextInput,
  Dialog,
} from "@mantine/core"
import { useTranslation } from "react-i18next"
import { UseMutationResult } from "@tanstack/react-query"
import { DatePickerInput } from "@mantine/dates"
import { InfinitePersonSingleSelect } from "@components/SelectFromQuery/InfinitePersonSingleSelect"
import { DefaultModal } from "@components/default-modal"
import { PriceInput } from "../../PriceInput"
import { ResourceObjectMonetaryValue } from "../types"

interface CreateValuationFormData {
  monetaryValue: number | undefined
  estimateDate: Date | null
  valuation: string
  estimatedByPersonId: number | null
  existingValuationId?: number
}

interface CreateValuationModalProps {
  opened: boolean
  onClose: () => void
  createValuationFormData: CreateValuationFormData
  setCreateValuationFormData: (data: CreateValuationFormData) => void
  createValuationMutation: UseMutationResult<
    any,
    Error,
    CreateValuationFormData
  >
  monetaryValues: ResourceObjectMonetaryValue[]
}

export function CreateValuationModal({
  opened,
  onClose,
  createValuationFormData,
  setCreateValuationFormData,
  createValuationMutation,
  monetaryValues,
}: CreateValuationModalProps) {
  const { t } = useTranslation("app_server")
  const [showReplaceDialog, setShowReplaceDialog] = React.useState(false)
  const [existingValuationForReplacement, setExistingValuationForReplacement] =
    React.useState<any>(null)
  const [priceInput, setPriceInput] = React.useState("")

  const handleSave = () => {
    if (!createValuationFormData.estimateDate) return

    const newDate = new Date(createValuationFormData.estimateDate)
    newDate.setHours(0, 0, 0, 0)

    const existingValuation = monetaryValues.find((m) => {
      if (!m.estimateDate) return false
      const existingDate = new Date(m.estimateDate)
      existingDate.setHours(0, 0, 0, 0)
      return existingDate.getTime() === newDate.getTime()
    })

    if (existingValuation) {
      setExistingValuationForReplacement(existingValuation)
      setShowReplaceDialog(true)
    } else {
      createValuationMutation.mutate(createValuationFormData)
    }
  }

  React.useEffect(() => {
    if (!opened) {
      setPriceInput("")
    }
  }, [opened])

  return (
    <>
      <DefaultModal
        opened={opened}
        onClose={() => {
          onClose()
          createValuationMutation.reset()
        }}
        title={t("Add")}
      >
        {opened && (
          <Stack gap="md">
            <PriceInput
              label={t("Price")}
              value={priceInput}
              onChange={(cleanedInput, numValue) => {
                setPriceInput(cleanedInput)
                setCreateValuationFormData({
                  ...createValuationFormData,
                  monetaryValue: numValue,
                })
              }}
              required
            />

            <DatePickerInput
              label={t("EstimateDate")}
              value={createValuationFormData.estimateDate ?? undefined}
              onChange={(value) =>
                setCreateValuationFormData({
                  ...createValuationFormData,
                  estimateDate: value ? new Date(value) : null,
                })
              }
            />

            <InfinitePersonSingleSelect
              label={t("EstimatedBy")}
              value={
                createValuationFormData.estimatedByPersonId?.toString() ?? ""
              }
              onSelectionChange={(value) =>
                setCreateValuationFormData({
                  ...createValuationFormData,
                  estimatedByPersonId: value ? parseInt(value) : null,
                })
              }
              searchable
            />

            <TextInput
              label={t("Note")}
              value={createValuationFormData.valuation}
              onChange={(e) =>
                setCreateValuationFormData({
                  ...createValuationFormData,
                  valuation: e.currentTarget.value,
                })
              }
              placeholder={t("EnterNote")}
            />

            {createValuationMutation.error && (
              <Text color="red" size="sm">
                {(createValuationMutation.error as Error).message}
              </Text>
            )}

            <Group justify="flex-end" gap="md">
              <Button variant="default" onClick={onClose}>
                {t("Cancel")}
              </Button>
              <Button
                onClick={handleSave}
                loading={createValuationMutation.isPending}
              >
                {t("Save")}
              </Button>
            </Group>
          </Stack>
        )}
      </DefaultModal>

      <Dialog
        opened={showReplaceDialog}
        onClose={() => setShowReplaceDialog(false)}
      >
        <Text size="sm" mb="md">
          {t("ValuationAtThisDateExistsDescription")}
        </Text>
        <Group justify="flex-end" gap="md">
          <Button variant="default" onClick={() => setShowReplaceDialog(false)}>
            {t("Cancel")}
          </Button>
          <Button
            onClick={() => {
              setShowReplaceDialog(false)
              createValuationMutation.mutate({
                ...createValuationFormData,
                existingValuationId:
                  existingValuationForReplacement?.resourceObjectMonetaryValueId,
              })
            }}
            loading={createValuationMutation.isPending}
          >
            {t("Replace")}
          </Button>
        </Group>
      </Dialog>
    </>
  )
}
